import { useCallback, useEffect, useState } from 'react';
import SearchInput from '@components/common/SearchInput';
import SelectInput from '@components/common/SelectInput';
import PageHeader from '@components/common/PageHeader';
import DataTable, {
    ColumnConfig,
} from '@components/common/DataTable/DataTable.tsx';
import { api } from '@/api/weather/weather/api.ts';
import {
    WeatherListItem,
    WeatherListRes,
    WeatherListReq,
} from '@/api/weather/weather/type.ts';
import DateRangePicker from '@components/common/DateRangePicker';
import { format } from 'date-fns';

const WeatherList = () => {
    const [areaOptions, setAreaOptions] = useState<
        { value: string; label: string }[]
    >([]);

    const [filters, setFilters] = useState<WeatherListReq>(() => {
        const today = new Date();
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(today.getDate() + 7);

        return {
            pageSize: 10,
            nowPage: 1,
            pageGroup: 10,
            areaIdx: 0,
            keyword: '',
            startDt: format(today, 'yyyy-MM-dd'),
            endDt: format(sevenDaysAgo, 'yyyy-MM-dd'),
        };
    });

    const updateFilter = (key: keyof WeatherListReq, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            ...(key !== 'nowPage' && { nowPage: 1 }),
        }));
    };

    const handleDateChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;

        setFilters(prev => ({
            ...prev,
            startDt: start ? format(start, 'yyyy-MM-dd') : undefined,
            endDt: end ? format(end, 'yyyy-MM-dd') : undefined,
            nowPage: 1,
        }));
    };

    const handleSearch = (value: string) => {
        updateFilter('keyword', value);
    };

    const handlePageChange = (page: number) => {
        updateFilter('nowPage', page);
    };

    const handlePageSizeChange = (pageSize: number) => {
        updateFilter('pageSize', pageSize);
    };

    const handleAreaChange = (areaIdx: string) => {
        updateFilter('areaIdx', parseInt(areaIdx));
    };

    const [weatherRes, setWeatherRes] = useState<WeatherListRes>();

    // 지역 코드 목록 로드
    const fetchAreaList = useCallback(async () => {
        try {
            const response = await api.weatherAreaList();

            const options = response.data.map(area => {
                const parts = [area.regionName1];
                if (area.regionName2) parts.push(area.regionName2);
                if (area.regionName3) parts.push(area.regionName3);

                return {
                    value: area.areaIdx.toString(),
                    label: parts.join(' > '),
                };
            });
            setAreaOptions(options);

            if (response.data.length > 0 && filters.areaIdx === 0) {
                setFilters(prev => ({
                    ...prev,
                    areaIdx: response.data[0].areaIdx,
                }));
            }
        } catch (error) {
            console.error('지역 코드 조회 실패:', error);
        }
    }, [filters.areaIdx]);

    // 기상 데이터 목록 로드
    const fetchWeatherList = useCallback(async () => {
        if (filters.areaIdx === 0) return;

        try {
            const response = await api.weatherList(filters);
            setWeatherRes(response);
        } catch (error) {
            console.error('기상 데이터 조회 실패:', error);
        }
    }, [filters]);

    // 실제 사용 가능한 날씨 상태값 매핑
    const WEATHER_MAP: Record<string, { text: string; path: string }> = {
        맑음: { text: '맑음', path: '/images/weather/Clear.png' },
        구름많음: {
            text: '구름많음',
            path: '/images/weather/ClearCloudy.png',
        },
        흐림: { text: '흐림', path: '/images/weather/Cloudy.png' },
        '구름많고 비': {
            text: '구름많고 비',
            path: '/images/weather/Rain.png',
        },
        '구름많고 눈': {
            text: '구름많고 눈',
            path: '/images/weather/Snow.png',
        },
        '구름많고 비/눈': {
            text: '구름많고 비/눈',
            path: '/images/weather/RainSnow.png',
        },
        '구름많고 소나기': {
            text: '구름많고 소나기',
            path: '/images/weather/Showers.png',
        },
        '흐리고 비': {
            text: '흐리고 비',
            path: '/images/weather/Rain.png',
        },
        '흐리고 눈': {
            text: '흐리고 눈',
            path: '/images/weather/Snow.png',
        },
        '흐리고 비/눈': {
            text: '흐리고 비/눈',
            path: '/images/weather/RainSnow.png',
        },
        '흐리고 소나기': {
            text: '흐리고 소나기',
            path: '/images/weather/Showers.png',
        },
        비: { text: '비', path: '/images/weather/Rain.png' },
        눈: { text: '눈', path: '/images/weather/Snow.png' },
        '비/눈': {
            text: '비/눈',
            path: '/images/weather/RainSnow.png',
        },
        소나기: {
            text: '소나기',
            path: '/images/weather/Showers.png',
        },
    };

    const columns: ColumnConfig<WeatherListItem>[] = [
        { key: 'weatherIdx', header: 'No' },
        { key: 'wDate', header: '날씨기준일자' },
        { key: 'dayOfWeek', header: '요일' },
        { key: 'regionName1', header: '시도' },
        { key: 'regionName2', header: '구군' },
        { key: 'regionName3', header: '읍면동' },
        {
            key: 'weatherAm',
            header: '오전날씨',
            render: value => {
                const info = WEATHER_MAP[value as string] || {
                    text: '알 수 없음',
                    path: '',
                };
                return (
                    <>
                        {info.path && (
                            <img
                                src={info.path}
                                className="weather-icon"
                                alt={value as string}
                            />
                        )}
                        {value}
                    </>
                );
            },
        },
        {
            key: 'weatherPm',
            header: '오후날씨',
            render: value => {
                const info = WEATHER_MAP[value as string] || {
                    text: '알 수 없음',
                    path: '',
                };
                return (
                    <>
                        {info.path && (
                            <img
                                src={info.path}
                                className="weather-icon"
                                alt={value as string}
                            />
                        )}
                        {value}
                    </>
                );
            },
        },
        {
            key: 'rainRateAm',
            header: '오전강수(%)',
            render: value => `${value}%`,
        },
        {
            key: 'rainRatePm',
            header: '오후강수(%)',
            render: value => `${value}%`,
        },
        { key: 'minTemp', header: '최저기온(°C)' },
        { key: 'maxTemp', header: '최고기온(°C)' },
        { key: 'wthRain', header: '일강수량(mm)' },
        { key: 'regDt', header: '등록일자' },
    ];

    // 지역 코드 목록 로드
    useEffect(() => {
        fetchAreaList();
    }, []);

    // 기상 데이터 목록 로드
    useEffect(() => {
        fetchWeatherList();
    }, [fetchWeatherList]);

    return (
        <>
            <PageHeader
                title="기상 데이터 관리"
                breadcrumbs={[
                    { label: '홈', href: '/' },
                    { label: '기상관리' },
                    { label: '기상 데이터 관리', active: true },
                ]}
            />

            <div className="row mt-4">
                <div className="col-xl-12">
                    <div className="card custom-card">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-12">
                                    <SelectInput
                                        label="지역"
                                        options={areaOptions}
                                        required={true}
                                        value={filters.areaIdx.toString()}
                                        onChange={handleAreaChange}
                                    />
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col-12 col-md-3">
                                    <DateRangePicker
                                        startDate={
                                            filters.startDt
                                                ? new Date(filters.startDt)
                                                : null
                                        }
                                        endDate={
                                            filters.endDt
                                                ? new Date(filters.endDt)
                                                : null
                                        }
                                        onChange={handleDateChange}
                                        placeholder="날짜 범위를 선택하세요"
                                    />
                                </div>
                                <div className="col-12 col-md-9">
                                    <SearchInput
                                        placeholder="시도, 구군, 동읍면을 입력해주세요."
                                        value={filters.keyword || ''}
                                        onSearch={handleSearch}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-xl-12">
                    {weatherRes && (
                        <DataTable<WeatherListItem>
                            data={weatherRes?.data}
                            columns={columns}
                            paging={weatherRes?.paging}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default WeatherList;
